import { BENCHMARK_MODES } from "Explore/Pages/KeywordBenchmark/benchmarkEntityAdapters";

export const COMPARE_QUEUE_STORAGE_KEY = "explore_compare_queue_v1";
export const COMPARE_QUEUE_TTL_MS = 60 * 60 * 1000;
export const COMPARE_QUEUE_MAX_ITEMS = 20;

/** @typedef {{ compareType: string, id: string, label: string, expiresAt: number }} CompareQueueItem */

const COMPARE_TYPE_TO_MODE = {
  faculty: BENCHMARK_MODES.RESEARCHER,
  institution: BENCHMARK_MODES.INSTITUTION,
  unit: BENCHMARK_MODES.UNIT,
  venue: BENCHMARK_MODES.VENUE,
};

function parseStored(json) {
  try {
    const parsed = JSON.parse(json);
    if (!parsed || !Array.isArray(parsed.items)) return { items: [] };
    return parsed;
  } catch {
    return { items: [] };
  }
}

export function purgeExpiredItems(items, now = Date.now()) {
  return (items || []).filter(
    (item) =>
      item &&
      typeof item.expiresAt === "number" &&
      item.expiresAt > now &&
      item.compareType &&
      item.id != null
  );
}

export function loadCompareQueue() {
  if (typeof window === "undefined") return [];
  const raw = window.localStorage.getItem(COMPARE_QUEUE_STORAGE_KEY);
  const { items: rawItems } = parseStored(raw || '{"items":[]}');
  const pruned = purgeExpiredItems(rawItems);
  if (pruned.length !== rawItems.length) {
    window.localStorage.setItem(
      COMPARE_QUEUE_STORAGE_KEY,
      JSON.stringify({ items: pruned })
    );
  }
  return pruned;
}

export function saveCompareQueue(items) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    COMPARE_QUEUE_STORAGE_KEY,
    JSON.stringify({ items })
  );
}

function itemKey(compareType, id) {
  return `${compareType}:${String(id)}`;
}

export function isInCompareQueue(items, compareType, id) {
  const k = itemKey(compareType, id);
  return (items || []).some((i) => itemKey(i.compareType, i.id) === k);
}

/**
 * @returns {{ items: CompareQueueItem[], action: 'added' | 'removed' | 'full' }}
 */
export function toggleCompareQueueItem(items, { compareType, id, label }) {
  const now = Date.now();
  const pruned = purgeExpiredItems(items, now);
  const k = itemKey(compareType, id);
  const idx = pruned.findIndex((i) => itemKey(i.compareType, i.id) === k);
  if (idx >= 0) {
    pruned.splice(idx, 1);
    return { items: pruned, action: "removed" };
  }
  if (pruned.length >= COMPARE_QUEUE_MAX_ITEMS) {
    return { items: pruned, action: "full" };
  }
  pruned.push({
    compareType,
    id: String(id),
    label: (label || String(id)).trim() || String(id),
    expiresAt: now + COMPARE_QUEUE_TTL_MS,
  });
  return { items: pruned, action: "added" };
}

export function removeCompareQueueItem(items, compareType, id) {
  const k = itemKey(compareType, id);
  const pruned = purgeExpiredItems(items);
  return pruned.filter((i) => itemKey(i.compareType, i.id) !== k);
}

export function clearCompareQueueItems() {
  saveCompareQueue([]);
}

/**
 * Maps saved compare rows into ResultsView `entityIdsByMode` / `entityLabelMaps` shape.
 * Faculty list ids align with researcher benchmark `researcher_ids` (same author entity).
 */
export function buildEntityStateFromCompareQueue(items) {
  const now = Date.now();
  const valid = purgeExpiredItems(items, now);
  const entityIdsByMode = {
    [BENCHMARK_MODES.INSTITUTION]: [],
    [BENCHMARK_MODES.RESEARCHER]: [],
    [BENCHMARK_MODES.UNIT]: [],
    [BENCHMARK_MODES.VENUE]: [],
  };
  const entityLabelMaps = {
    [BENCHMARK_MODES.INSTITUTION]: {},
    [BENCHMARK_MODES.RESEARCHER]: {},
    [BENCHMARK_MODES.UNIT]: {},
    [BENCHMARK_MODES.VENUE]: {},
  };
  const seen = {
    [BENCHMARK_MODES.INSTITUTION]: new Set(),
    [BENCHMARK_MODES.RESEARCHER]: new Set(),
    [BENCHMARK_MODES.UNIT]: new Set(),
    [BENCHMARK_MODES.VENUE]: new Set(),
  };

  for (const item of valid) {
    const mode = COMPARE_TYPE_TO_MODE[item.compareType];
    if (!mode) continue;
    const sid = String(item.id);
    if (seen[mode].has(sid)) continue;
    seen[mode].add(sid);
    entityIdsByMode[mode].push(sid);
    entityLabelMaps[mode][sid] = item.label || sid;
  }

  return { entityIdsByMode, entityLabelMaps };
}

/** Shape expected by Keyword Benchmark `ResultsView` state initializers. */
export function getEntityPickerInitialStateFromCompareQueue() {
  const seeded = buildEntityStateFromCompareQueue(loadCompareQueue());
  return {
    entityIdsByMode: {
      [BENCHMARK_MODES.INSTITUTION]: [
        ...seeded.entityIdsByMode[BENCHMARK_MODES.INSTITUTION],
      ],
      [BENCHMARK_MODES.RESEARCHER]: [
        ...seeded.entityIdsByMode[BENCHMARK_MODES.RESEARCHER],
      ],
      [BENCHMARK_MODES.UNIT]: [...seeded.entityIdsByMode[BENCHMARK_MODES.UNIT]],
      [BENCHMARK_MODES.VENUE]: [...seeded.entityIdsByMode[BENCHMARK_MODES.VENUE]],
    },
    entityLabelMaps: {
      [BENCHMARK_MODES.INSTITUTION]: {
        ...seeded.entityLabelMaps[BENCHMARK_MODES.INSTITUTION],
      },
      [BENCHMARK_MODES.RESEARCHER]: {
        ...seeded.entityLabelMaps[BENCHMARK_MODES.RESEARCHER],
      },
      [BENCHMARK_MODES.UNIT]: { ...seeded.entityLabelMaps[BENCHMARK_MODES.UNIT] },
      [BENCHMARK_MODES.VENUE]: {
        ...seeded.entityLabelMaps[BENCHMARK_MODES.VENUE],
      },
    },
  };
}
